import { useCallback, useEffect, useRef, useState } from 'react';

import type { CreateGitBindingBody, GitBindingResponse, UpdateGitBindingBody } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

type Status = 'idle' | 'loading' | 'success' | 'error';

/** Git bindings for a specific tenant (which repo/branch/credential it points at). */
export function useGitBindings(tenantId: string | null) {
  const apiClient = useApiClient();
  const [bindings, setBindings] = useState<GitBindingResponse[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const inFlight = useRef(false);

  const reload = useCallback(() => {
    if (!tenantId || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    apiClient
      .listGitBindings(tenantId)
      .then((res) => {
        setBindings(res.data);
        setStatus('success');
        setError(undefined);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load git bindings');
        setStatus('error');
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [apiClient, tenantId]);

  useEffect(() => {
    if (tenantId) reload();
    else {
      setBindings([]);
      setStatus('idle');
    }
  }, [tenantId, reload]);

  const createBinding = useCallback(
    async (body: CreateGitBindingBody) => {
      if (!tenantId) throw new Error('no tenant selected');
      const b = await apiClient.createGitBinding(tenantId, body);
      reload();
      return b;
    },
    [apiClient, tenantId, reload],
  );

  const updateBinding = useCallback(
    async (bindingId: string, body: UpdateGitBindingBody) => {
      if (!tenantId) throw new Error('no tenant selected');
      const b = await apiClient.updateGitBinding(tenantId, bindingId, body);
      reload();
      return b;
    },
    [apiClient, tenantId, reload],
  );

  const deleteBinding = useCallback(
    async (bindingId: string) => {
      if (!tenantId) throw new Error('no tenant selected');
      await apiClient.deleteGitBinding(tenantId, bindingId);
      reload();
    },
    [apiClient, tenantId, reload],
  );

  return { bindings, status, error, reload, createBinding, updateBinding, deleteBinding };
}
